import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from "@heroicons/react/24/outline";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { Link } from "react-router-dom";
import { useEffect, useState, useRef } from "react";
import classNames from "classnames";

interface SuggestionsInputProps {
  id: string;
  label: string;
  placeholder: string;
  suggestions: any[];
  selectedValue: SelectedObject | null;
  onSelect: (selectedId: string) => void;
  suggestionLabelEntityProperty: string;
  disabled?: boolean;
  infoText?: string;
  entityName: string;
  createEntityLink: string;
  customEmptyStateMessage?: string;
  customNoMatchingSearchesMessage?: string;
  customCreateEntityLinkMessage?: string;
}

interface SelectedObject {
  _id: string;
  name: string;
}

const SuggestionsInput: React.FC<SuggestionsInputProps> = ({
  id,
  label,
  placeholder,
  suggestions,
  selectedValue,
  onSelect,
  suggestionLabelEntityProperty,
  disabled,
  infoText,
  entityName,
  createEntityLink,
  customEmptyStateMessage,
  customNoMatchingSearchesMessage,
  customCreateEntityLinkMessage,
}) => {
  const [inputValue, setInputValue] = useState("");
  const [filteredSuggestions, setFilteredSuggestions] = useState<any[]>([]);
  const [isSuggestionsDropdownOpen, setIsSuggestionsDropdownOpen] =
    useState(false);
  const [showClearInput, setShowClearInput] = useState(false);
  const [selected, setSelected] = useState<SelectedObject | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const [isInputFocused, setIsInputFocused] = useState(false);

  useEffect(() => {
    setSelected(selectedValue);
    setInputValue(selectedValue ? selectedValue.name : "");
  }, [selectedValue]);

  useEffect(() => {
    if (inputValue) {
      setShowClearInput(true);
    } else {
      setShowClearInput(false);
    }
  }, [inputValue]);

  const handleInputChange = (e: any) => {
    const value = e.target.value;
    setInputValue(value);

    // Filter the suggestions based on the user's input
    const filtered = suggestions.filter((suggestion) =>
      suggestion[suggestionLabelEntityProperty]
        .toLowerCase()
        .includes(value.toLowerCase())
    );
    setFilteredSuggestions(filtered);
    if (!isSuggestionsDropdownOpen) {
      setIsSuggestionsDropdownOpen(true);
    }
  };

  const handleInputFocus = () => {
    setIsInputFocused(true);

    const filtered = suggestions.filter((suggestion) =>
      suggestion[suggestionLabelEntityProperty]
        .toLowerCase()
        .includes(inputValue.toLowerCase())
    );
    setFilteredSuggestions(filtered);

    // Display all suggestions when input is initially focused
    if (inputValue === "") {
      setFilteredSuggestions(suggestions);
    }
  };

  const handleInputBlur = (event: any) => {
    const relatedTarget = event.relatedTarget;
    if (
      relatedTarget &&
      relatedTarget.getAttribute("data-type") === "suggestion"
    ) {
      const suggestionString = relatedTarget.innerText;
      setInputValue(suggestionString);
      const suggestionId = relatedTarget.getAttribute("data-suggestion-id");
      setSelected({ _id: suggestionId, name: suggestionString });
      onSelect(suggestionId);
      setIsSuggestionsDropdownOpen(false);
    } else if (
      relatedTarget &&
      relatedTarget.getAttribute("type") === "workout-templates-link"
    ) {
      return;
    } else if (selected) {
      setInputValue(selected.name);
      onSelect(selected._id);
      setIsSuggestionsDropdownOpen(false);
    } else {
      setInputValue("");
      onSelect("");
      setIsSuggestionsDropdownOpen(false);
    }
    setIsInputFocused(false);
  };

  const handleInputClicked = () => {
    if (isInputFocused && isSuggestionsDropdownOpen) {
      setIsSuggestionsDropdownOpen(false);
    } else if (isInputFocused && !isSuggestionsDropdownOpen) {
      setIsSuggestionsDropdownOpen(true);
    }
  };

  const handleInputCleared = () => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
    setFilteredSuggestions(suggestions);
    setIsSuggestionsDropdownOpen(true);
    setInputValue("");
    setSelected(null);
    onSelect("");
  };

  window.onblur = function () {
    if (document.activeElement instanceof HTMLElement) {
      document.activeElement.blur();
    }
  };

  return (
    <div
      className={classNames(`relative`, {
        "opacity-40": disabled,
      })}
    >
      <label
        htmlFor={id}
        className="block mb-1 text-sm font-medium text-gray-700"
      >
        {label}
      </label>
      <div className="relative">
        <MagnifyingGlassIcon
          className="h-5 w-auto text-gray-400 absolute top-[19px] -translate-y-1/2 left-3 pointer-events-none"
          aria-hidden="true"
        />
        {showClearInput && (
          <XMarkIcon
            className="h-5 w-auto text-gray-400 absolute top-[19px] -translate-y-1/2 right-[48px]"
            aria-hidden="true"
            onClick={handleInputCleared}
          />
        )}
        <div className="w-[1px] h-[24px] bg-gray-400 absolute right-[40px] top-[19px] -translate-y-1/2 pointer-events-none"></div>
        <ChevronDownIcon
          className="h-5 w-auto text-gray-400 absolute right-3 top-[19px] -translate-y-1/2 pointer-events-none"
          aria-hidden="true"
        />
        <input
          className={`block w-full px-10 rounded-md border-gray-300 shadow-sm focus:border-indigo-600 focus:outline-none text-sm ${
            showClearInput && "pr-[74px]"
          }`}
          type="text"
          value={inputValue}
          onChange={handleInputChange}
          onFocus={handleInputFocus}
          onBlur={handleInputBlur}
          onClick={handleInputClicked}
          placeholder={placeholder}
          ref={inputRef}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              const target = e.target as HTMLElement;
              target.blur();
            }
          }}
          disabled={disabled}
        />
        {isSuggestionsDropdownOpen && (
          <div className="absolute z-50 w-full top-[calc(100%+8px)]">
            <div className="w-full flex flex-col bg-white shadow-lg ring-1 ring-black ring-opacity-5 rounded-md overflow-y-scroll max-h-[234px] py-1">
              {filteredSuggestions.map((suggestion, index) => {
                return (
                  <div
                    className={classNames(
                      `relative text-left px-3 py-[9px] text-sm w-full hover:cursor-pointer flex flex-row`,
                      {
                        "bg-indigo-200":
                          selected && suggestion._id === selected._id,
                      }
                    )}
                    key={suggestion._id}
                    data-suggestion-id={suggestion._id}
                    data-type="suggestion"
                    tabIndex={0}
                  >
                    <span className="ml-[28px] text-gray-700">
                      {suggestion[suggestionLabelEntityProperty]}
                    </span>
                  </div>
                );
              })}
              {suggestions.length !== 0 && filteredSuggestions.length === 0 && (
                <div className="w-full flex flex-col items-center gap-y-2 text-center p-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    {`No ${entityName}s Found`}
                  </h3>
                  <p className="text-sm text-gray-700">
                    {`"${inputValue}" ${
                      customNoMatchingSearchesMessage
                        ? customNoMatchingSearchesMessage
                        : `did not match any of your ${entityName.toLocaleLowerCase()}s`
                    }. Please try again or `}
                    <Link
                      to={createEntityLink}
                      className="text-indigo-500 underline"
                      type="workout-templates-link"
                    >
                      {customCreateEntityLinkMessage ||
                        `create a new ${entityName.toLocaleLowerCase()}`}
                    </Link>
                  </p>
                </div>
              )}
              {suggestions.length === 0 && (
                <div className="w-full flex flex-col items-center gap-y-2 text-center p-3">
                  <h3 className="text-sm font-medium text-gray-700">
                    {`No ${entityName}s Found`}
                  </h3>
                  <p className="text-sm text-gray-700">
                    {customEmptyStateMessage ||
                      `You do not currently have any ${entityName.toLocaleLowerCase()}s.`}
                    <br />
                    <Link
                      to={createEntityLink}
                      className="text-sm text-indigo-500 underline"
                      type="workout-templates-link"
                    >
                      {customCreateEntityLinkMessage ||
                        `Create a new ${entityName.toLocaleLowerCase()}`}
                    </Link>
                  </p>
                </div>
              )}
            </div>
          </div>
        )}
      </div>
      {infoText && (
        <p className="grid items-start grid-cols-[auto,1fr] gap-1 mt-2 text-sm text-gray-500 whitespace-pre-wrap">
          <InformationCircleIcon
            className="h-4 auto text-gray-500"
            aria-hidden="true"
          />
          {infoText}
        </p>
      )}
    </div>
  );
};

export default SuggestionsInput;
